import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { identityGateway } from '../services';
import { ApiError, type LoginOutcome, type MeResponse } from '../types';
import { IdentityContext, type IdentityContextValue } from './IdentityContext';
import { friendlyErrorMessage } from '../../../shared/friendlyError';

export function IdentityProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [wantsCompanyPicker, setWantsCompanyPicker] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [isAutoSelectingCompany, setIsAutoSelectingCompany] = useState(false);
  // Mirrors `me`, but read synchronously (not via React state, which only commits on the next render) so
  // refresh()'s catch block below can tell "we WERE authenticated a moment ago" from "we never were" —
  // the actual signal `sessionExpired` is built on. Kept in lockstep with every place `me` changes.
  const wasAuthenticatedRef = useRef(false);
  // Guards the single-company auto-select block below against a second concurrent refresh() (e.g. two
  // effects firing close together) starting a second switch attempt while one is already in flight — a
  // ref rather than state because it must be read/set synchronously, before the first `await` below.
  const autoSelectInFlightRef = useRef(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const nextMe = await identityGateway.getMe();
      wasAuthenticatedRef.current = true;
      setMe(nextMe);
      setSessionExpired(false);
    } catch (err) {
      // A 401 here specifically (not a network/other error — see ApiError's own doc comment) means the
      // access token is gone AND the refresh-token retry HttpIdentityGateway already attempted also
      // failed, i.e. this session's credentials are genuinely no longer valid, not merely "never signed
      // in." Only surface that as `sessionExpired` when this provider had actually established a session
      // before — a cold app boot's first (and only) getMe() 401 is not an expired session, it's simply
      // "not signed in yet," and must not show that message.
      if (err instanceof ApiError && err.status === 401 && wasAuthenticatedRef.current) {
        setSessionExpired(true);
      }
      wasAuthenticatedRef.current = false;
      setMe(null);
    } finally {
      setLoading(false);
      // Only ever transitions true → false, on whichever refresh() call happens to be the first one
      // (app boot). A no-op on every later refresh() (post-login, post-switch-company, etc).
      setInitializing(false);
    }
  }, []);

  // Single-company post-login auto-selection (EPN-47 product decision A). A user with exactly one active
  // membership and no company context established yet should not have to click through CompanyPickerPage
  // to choose the only company they can access. This still goes through the SAME secure
  // identityGateway.switchCompany()/POST /api/me/switch-company path an explicit switch uses — the
  // server-side membership re-verification and token issuance remain the one place company context is
  // ever granted; nothing here sets activeCompanyId directly or bypasses that call.
  //
  // Deliberately a SEPARATE effect reacting to `me`, not inlined into refresh() itself — refresh() (and
  // therefore login()) must keep resolving at its ORIGINAL speed once the user is merely authenticated,
  // before any company context exists. Nesting the switch-company round trip inside refresh() delayed
  // login()'s own resolution, which raced against LoginPage's own post-login `returnTo` navigation (a
  // real, observed bug during EPN-47's own testing: the app could transiently render the "/login" route
  // table entry — which redirects an authenticated user to /dashboard — before LoginPage's imperative
  // navigate(returnTo) ran, permanently losing the intended destination). Decoupling them restores
  // login()'s original fast resolution (so `returnTo` navigation fires immediately, exactly as before
  // EPN-47), while this effect performs the auto-selection afterward, independently.
  useEffect(() => {
    if (!me || me.activeCompanyId || me.memberships.length !== 1 || autoSelectInFlightRef.current) {
      return;
    }
    autoSelectInFlightRef.current = true;
    setIsAutoSelectingCompany(true);
    void (async () => {
      try {
        await identityGateway.switchCompany(me.memberships[0]!.membershipId);
        await refresh();
      } catch (err) {
        // Left with activeCompanyId still null — App.tsx's existing gate falls back to CompanyPickerPage,
        // which already renders this same `error` and still offers the one membership as a manual retry.
        // Deliberately NOT retried automatically here: a failed auto-select attempt must never become a
        // retry loop.
        setError(friendlyErrorMessage(err, 'Could not switch company.'));
      } finally {
        setIsAutoSelectingCompany(false);
        autoSelectInFlightRef.current = false;
      }
    })();
  }, [me, refresh]);

  const login = useCallback(async (email: string, password: string, rememberMe = false): Promise<LoginOutcome> => {
    setLoading(true);
    setError(null);
    try {
      const outcome = await identityGateway.login(email, password, rememberMe);
      if (outcome.kind === 'success') {
        // Unchanged existing behavior: populate `me` via the same refresh() a plain, no-MFA login always
        // used. For 'mfaRequired'/'locked', no session cookies exist yet — refreshing would just 401.
        await refresh();
      }
      return outcome;
    } catch (err) {
      // A 401 from /login means bad credentials, not an expired session — friendlyErrorMessage's 401 copy
      // would be wrong here (same reasoning as LoginPage's own handling).
      setError(
        err instanceof ApiError && err.status === 401
          ? 'The email or password you entered is incorrect.'
          : friendlyErrorMessage(err, 'Login failed.'),
      );
      throw err;
    } finally {
      setLoading(false);
    }
  }, [refresh]);

  const completeMfaVerification = useCallback(
    async (challengeToken: string, method: string, code: string, rememberDevice: boolean) => {
      setLoading(true);
      setError(null);
      try {
        const result = await identityGateway.mfaVerify(challengeToken, method, code, rememberDevice);
        // Same refresh()-driven `me` population login()'s success case triggers — a verified MFA
        // challenge is a fully completed login in every way that matters to the rest of this app.
        await refresh();
        return result;
      } catch (err) {
        setError(friendlyErrorMessage(err, 'Verification failed.'));
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [refresh],
  );

  const logout = useCallback(async () => {
    await identityGateway.logout();
    wasAuthenticatedRef.current = false;
    setMe(null);
  }, []);

  const switchCompany = useCallback(async (membershipId: string) => {
    setError(null);
    try {
      await identityGateway.switchCompany(membershipId);
      setWantsCompanyPicker(false);
      await refresh();
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not switch company.'));
      throw err;
    }
  }, [refresh]);

  const changePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    await identityGateway.changePassword(currentPassword, newPassword);
    // Every refresh token was just revoked server-side, including this session's own — the client must
    // treat this exactly like a forced sign-out, not merely a settings save. This is a deliberate,
    // user-initiated sign-out, not an expired session — never sets `sessionExpired`.
    wasAuthenticatedRef.current = false;
    setMe(null);
  }, []);

  const showCompanyPicker = useCallback(() => setWantsCompanyPicker(true), []);

  const value = useMemo<IdentityContextValue>(
    () => ({
      me,
      loading,
      initializing,
      error,
      wantsCompanyPicker,
      sessionExpired,
      isAutoSelectingCompany,
      login,
      completeMfaVerification,
      logout,
      switchCompany,
      changePassword,
      showCompanyPicker,
      refresh,
    }),
    [
      me,
      loading,
      initializing,
      error,
      wantsCompanyPicker,
      sessionExpired,
      isAutoSelectingCompany,
      login,
      completeMfaVerification,
      logout,
      switchCompany,
      changePassword,
      showCompanyPicker,
      refresh,
    ],
  );

  return <IdentityContext.Provider value={value}>{children}</IdentityContext.Provider>;
}
